import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO, Breadcrumb } from '@/components/seo';
import { FAQSchema } from '@/components/seo/Schema';
import {
  ArrowRight,
  Loader2,
  Cpu,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Droplet,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Info,
  FlaskConical,
} from 'lucide-react';
import { buttonStyles } from '@/components/ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface FAQItem {
  q: string;
  a: string;
}

interface ModelData {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  category: string;
  type: string | null;
  description: string | null;
  longDescription: string | null;
  faq: FAQItem[] | null;
  avgDetectionRate: number | null;
  logoUrl: string | null;
}

interface RelatedModel {
  slug: string;
  name: string;
  vendor: string;
  category: string;
  avgDetectionRate: number | null;
  logoUrl: string | null;
}

interface WatermarkData {
  slug: string;
  name: string;
  developer: string | null;
  type: string | null;
  isRemovable: boolean;
  isDefault: boolean;
}

interface ServiceData {
  slug: string;
  name: string;
  vendor: string;
  url: string | null;
  isDefault: boolean;
  logoUrl: string | null;
}

interface DetectorData {
  slug: string;
  name: string;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
}

export function ModelPage() {
  const { slug } = useParams<{ slug: string }>();
  const [model, setModel] = useState<ModelData | null>(null);
  const [watermarks, setWatermarks] = useState<WatermarkData[]>([]);
  const [services, setServices] = useState<ServiceData[]>([]);
  const [detectors, setDetectors] = useState<DetectorData[]>([]);
  const [bestDetectors, setBestDetectors] = useState<DetectorData[]>([]);
  const [worstDetectors, setWorstDetectors] = useState<DetectorData[]>([]);
  const [relatedModels, setRelatedModels] = useState<RelatedModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/pages/models/${slug}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load model');
        }

        setModel(data.model);
        setWatermarks(data.watermarks || []);
        setServices(data.services || []);
        setDetectors(data.detectors || []);
        setBestDetectors(data.bestDetectors || []);
        setWorstDetectors(data.worstDetectors || []);
        setRelatedModels(data.relatedModels || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error || !model) {
    return <Navigate to="/" replace />;
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'AI Models', href: '/models' },
    { label: model.name },
  ];

  // Calculate detection stats
  const undetectedCount = detectors.filter(d => d.successRate < 30).length;

  return (
    <>
      <SEO
        title={`${model.name} AI Detection Rate — How Well Do Detectors Catch ${model.vendor} Images? | AI Detector Arena`}
        description={model.description || `How detectable are ${model.name} images? Average detection rate of ${model.avgDetectionRate !== null ? Math.round(model.avgDetectionRate) + '%' : 'N/A'} across ${detectors.length} AI detectors. Independent benchmark results.`}
        canonical={`/models/${model.slug}`}
      />

      {/* FAQ Schema for rich snippets */}
      {model.faq && model.faq.length > 0 && (
        <FAQSchema
          questions={model.faq.map(item => ({
            question: item.q,
            answer: item.a,
          }))}
        />
      )}

      {/* Hero */}
      <section className="section bg-gradient-to-b from-purple-50 via-white to-white">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="max-w-4xl mx-auto text-center mt-8">
            {model.logoUrl && (
              <img
                src={model.logoUrl}
                alt={`${model.name} logo`}
                className="w-16 h-16 object-contain mx-auto mb-4"
              />
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {model.name} — AI Detection Benchmark
            </h1>

            {model.description && (
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                {model.description}
              </p>
            )}

            {/* Quick Stats */}
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <div className={`text-3xl font-bold ${
                  model.avgDetectionRate !== null && model.avgDetectionRate >= 70 ? 'text-red-500' :
                  model.avgDetectionRate !== null && model.avgDetectionRate >= 40 ? 'text-yellow-500' :
                  'text-green-500'
                }`}>
                  {model.avgDetectionRate !== null ? `${Math.round(model.avgDetectionRate)}%` : '—'}
                </div>
                <div className="text-sm text-gray-500">Avg Detection Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-500">{detectors.length}</div>
                <div className="text-sm text-gray-500">Detectors Tested</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-500">{undetectedCount}</div>
                <div className="text-sm text-gray-500">Undetected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verdict / Summary */}
      {model.avgDetectionRate !== null && detectors.length > 0 && (() => {
        const sorted = [...detectors].sort((a, b) => b.successRate - a.successRate);
        const bestDetector = sorted[0];
        const worstDetector = sorted[sorted.length - 1];
        const rateComment = model.avgDetectionRate !== null
          ? model.avgDetectionRate >= 80
            ? 'highly detectable by most AI detectors'
            : model.avgDetectionRate >= 50
              ? 'moderately detectable across AI detectors'
              : 'difficult to detect for most AI detectors'
          : null;

        return (
          <section className="section bg-white py-8">
            <div className="container">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-6 h-6 text-blue-500" />
                  Verdict
                </h2>
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed">
                    {model.name} images are {rateComment} with an average detection rate of{' '}
                    <strong>{Math.round(model.avgDetectionRate!)}%</strong> across{' '}
                    <strong>{detectors.length}</strong> tested detectors.
                    {detectors.length > 1 && (
                      <>
                        {' '}<Link to={`/detectors/${bestDetector.slug}`} className="text-blue-600 hover:underline font-medium">
                          {bestDetector.name}
                        </Link>{' '}
                        performs best at catching {model.name} content ({bestDetector.successRate}% detection),
                        while{' '}
                        <Link to={`/detectors/${worstDetector.slug}`} className="text-blue-600 hover:underline font-medium">
                          {worstDetector.name}
                        </Link>{' '}
                        struggles most (only {worstDetector.successRate}% detected).
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Detection Results */}
      {detectors.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-500" />
                Detection Results
              </h2>

              <div className="space-y-3">
                {detectors.map((detector) => (
                  <Link
                    key={detector.slug}
                    to={`/detectors/${detector.slug}`}
                    className="flex items-center justify-between bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {detector.successRate >= 70 ? (
                        <CheckCircle className="w-5 h-5 text-red-500" />
                      ) : detector.successRate < 30 ? (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                      )}
                      <span className="font-medium text-gray-900">{detector.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      {detector.avgScore !== null && (
                        <span className="text-sm text-gray-500">
                          Avg: {detector.avgScore}%
                        </span>
                      )}
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            detector.successRate >= 70 ? 'bg-red-500' :
                            detector.successRate >= 40 ? 'bg-yellow-500' :
                            'bg-gray-400'
                          }`}
                          style={{ width: `${detector.successRate}%` }}
                        />
                      </div>
                      <span className={`text-sm w-16 text-right font-medium ${
                        detector.successRate >= 70 ? 'text-red-500' :
                        detector.successRate >= 40 ? 'text-yellow-500' :
                        'text-gray-500'
                      }`}>
                        {detector.successRate >= 70 ? 'Detected' :
                         detector.successRate < 30 ? 'Missed' : 'Partial'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Best/Worst Detectors */}
      {(bestDetectors.length > 0 || worstDetectors.length > 0) && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Best & Worst Detectors for {model.name}
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Best Detectors (high success = catches the model) */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-red-500" />
                    Highest Detection Rate
                  </h3>
                  <div className="space-y-3">
                    {bestDetectors.map((d, i) => (
                      <Link
                        key={d.slug}
                        to={`/detectors/${d.slug}`}
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-red-500 font-bold">#{i + 1}</span>
                          <span className="font-medium text-gray-900">{d.name}</span>
                        </div>
                        <span className="text-red-600 font-semibold">{d.successRate}%</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Worst Detectors (low success = model not detected) */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-gray-500" />
                    Lowest Detection Rate
                  </h3>
                  <div className="space-y-3">
                    {worstDetectors.map((d, i) => (
                      <Link
                        key={d.slug}
                        to={`/detectors/${d.slug}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-gray-500 font-bold">#{i + 1}</span>
                          <span className="font-medium text-gray-900">{d.name}</span>
                        </div>
                        <span className="text-gray-600 font-semibold">{d.successRate}%</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Watermarks */}
      {watermarks.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Droplet className="w-6 h-6 text-purple-500" />
                Watermarks Used by {model.name}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {watermarks.map((wm) => (
                  <Link
                    key={wm.slug}
                    to={`/watermarks/${wm.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{wm.name}</h3>
                      <p className="text-sm text-gray-500">
                        {wm.developer} | {wm.type}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {wm.isRemovable ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Removable
                        </span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                          Partial
                        </span>
                      )}
                      {wm.isDefault && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {services.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Services Using {model.name}
              </h2>

              <div className="grid md:grid-cols-3 gap-4">
                {services.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="bg-white rounded-xl p-4 hover:shadow-md transition-shadow flex items-start gap-3"
                  >
                    {service.logoUrl && (
                      <img src={service.logoUrl} alt={service.name} className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{service.name}</h3>
                      <p className="text-sm text-gray-500">{service.vendor}</p>
                      {service.url && (
                        <span className="text-xs text-blue-600 flex items-center gap-1 mt-2">
                          Visit <ExternalLink className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benchmark Methodology */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-purple-500" />
              Benchmark Methodology
            </h2>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                {model.name} images were tested across{' '}
                <strong>{detectors.length}</strong> AI detectors
                using a curated dataset of AI-generated and real photographs.
                {watermarks.length > 0 && (
                  <> The model uses <strong>{watermarks.length}</strong> watermark type{watermarks.length > 1 ? 's' : ''} ({watermarks.map(w => w.name).join(', ')}).</>
                )}
              </p>
              <p className="text-gray-700 leading-relaxed">
                Each detector classifies images as AI-generated or real. We measure the{' '}
                <strong>detection rate</strong> — the percentage of {model.name} images correctly
                identified as AI-generated. Higher rates mean the detector catches {model.name} content more reliably.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                See full benchmark methodology
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Text */}
      {model.longDescription && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-3xl mx-auto prose prose-gray">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {model.longDescription}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {model.faq && model.faq.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {model.faq.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="font-medium text-gray-900">{item.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5 text-gray-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Models */}
      {relatedModels.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Related AI Models
              </h2>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedModels.map((rm) => (
                  <Link
                    key={rm.slug}
                    to={`/models/${rm.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors flex items-start gap-3"
                  >
                    {rm.logoUrl ? (
                      <img src={rm.logoUrl} alt={rm.name} className="w-8 h-8 object-contain flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Cpu className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{rm.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{rm.vendor}</p>
                      {rm.avgDetectionRate !== null && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          rm.avgDetectionRate >= 70 ? 'bg-red-100 text-red-700' :
                          rm.avgDetectionRate >= 40 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {Math.round(rm.avgDetectionRate)}% detection
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Test {model.name} Images in the Arena
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Upload a {model.name} image and see how detectors perform in real time.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/arena"
              className={buttonStyles({
                variant: 'secondary',
                size: 'lg',
                className: 'bg-white text-primary-600 hover:bg-gray-100',
              })}
            >
              Start a Battle
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/models"
              className={buttonStyles({
                variant: 'secondary',
                size: 'lg',
                className: 'bg-primary-500 text-white hover:bg-primary-400 border-primary-400',
              })}
            >
              Compare All Models
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default ModelPage;
